import React from 'react'
import type { Company, Development } from '@homeviews/models'
import {
  REVIEWER_TYPES,
  RATING_TYPES,
  CHART_COLORS_NON_BENCHMARK_ARRAY,
  CHART_COLORS,
  CHART_BRAND_COLORS,
  REVIEWER_AGES,
  SUSTAINABILITY,
} from './settings'

export const transformReviewerTypesData = (rawData: any) => {
  const preparedData = []
  const reviewerTypesOptions = []

  if (!rawData) {
    return [preparedData, reviewerTypesOptions]
  }

  Object.keys(rawData).forEach((id: string, index: number) => {
    const item = rawData[id]
    if (REVIEWER_TYPES?.[id]?.label) {
      preparedData.push({
        id: id,
        label: REVIEWER_TYPES?.[id]?.label,
        value: item?.count || 0,
        color: CHART_COLORS_NON_BENCHMARK_ARRAY[index] || 'black',
      })
      reviewerTypesOptions.push({
        value: id,
        label: REVIEWER_TYPES?.[id]?.label,
      })
    }
  })

  return [preparedData, reviewerTypesOptions]
}

export const transformDevelopmentScoresData = (rawData: any) => {
  const preparedData = []
  const developmentOptions = []
  let singleDevelopmentScores = false

  if (!rawData) {
    return [preparedData, developmentOptions]
  }

  if (rawData?.length === 1) {
    singleDevelopmentScores = true
  }

  if (singleDevelopmentScores) {
    rawData.map((development: any) => {
      preparedData.push({
        id: 'Management Rating',
        value: development.rating_management,
      })
      preparedData.push({
        id: 'Value Rating',
        value: development.rating_value,
      })
      preparedData.push({
        id: 'Location Rating',
        value: development.rating_location,
      })
      preparedData.push({
        id: 'Facilities Rating',
        value: development.rating_facilities,
      })
      preparedData.push({
        id: 'Design Rating',
        value: development.rating_design,
      })
      preparedData.push({
        id: 'Overall Review Rating',
        value: development.rating,
      })
    })
  } else {
    rawData.map((development: any) => {
      preparedData.push({
        id: development.name,
        rating: development.rating,
        rating_design: development.rating_design,
        rating_facilities: development.rating_facilities,
        rating_location: development.rating_location,
        rating_management: development.rating_management,
        rating_value: development.rating_value,
      })
    })
  }

  return [preparedData, singleDevelopmentScores]
}

export const transformRatingsReviewersTypeData = (rawData: any) => {
  if (!rawData) {
    return []
  }

  const preparedData = []

  Object.keys(RATING_TYPES).map((ratingTypeId: string) => {
    const partialData = {
      id: ratingTypeId,
    }
    Object.keys(REVIEWER_TYPES).map((reviewerTypeId: string) => {
      if (rawData?.[reviewerTypeId]?.[ratingTypeId]) {
        partialData[reviewerTypeId] = rawData[reviewerTypeId][ratingTypeId]
      }
    })

    if (Object.keys(partialData).length > 2) {
      preparedData.push(partialData)
    }
  })

  return preparedData
}

interface HeadCell {
  id: string
  label: string
  sortable?: boolean
  width?: number
}

export const headCells: HeadCell[] = [
  {
    id: 'development_name',
    label: 'Development',
    width: 300,
  },
  {
    id: 'verified_resident_reviews',
    label: 'Verified resident reviews',
    width: 115,
  },
  {
    id: 'excellence',
    label: 'Rating of excellence',
    width: 115,
  },
  {
    id: 'design',
    label: 'Design',
    width: 115,
  },
  {
    id: 'facilities',
    label: 'Facilities',
    width: 115,
  },
  {
    id: 'location',
    label: 'Location',
    width: 115,
  },
  {
    id: 'value',
    label: 'Value',
    width: 115,
  },
  {
    id: 'management',
    label: 'Management',
    width: 115,
  },
]

interface IAward {
  id: number
  verified_resident_reviews: number
  development_name: string
  excellence: number
  facilities: number
  location: number
  management: number
  value: number
  design: number
}

const AwardCell = ({ awarded }) => {
  if (!awarded) {
    return <span />
  }
  return (
    <span className="l-table__cell--award">
      <img src="assets/icons/medium/tick.svg" alt="tick" />
    </span>
  )
}

function createData(award: IAward) {
  return [
    <React.Fragment key="id">{award.id}</React.Fragment>,
    <React.Fragment key="name">{award.development_name}</React.Fragment>,
    <span key="count" className="l-table__cell--awardCount">
      {award.verified_resident_reviews}
    </span>,
    <AwardCell key="excellence" awarded={Number(award.excellence)} />,
    <AwardCell key="design" awarded={Number(award.design)} />,
    <AwardCell key="facilities" awarded={Number(award.facilities)} />,
    <AwardCell key="location" awarded={Number(award.location)} />,
    <AwardCell key="value" awarded={Number(award.value)} />,
    <AwardCell key="management" awarded={Number(award.management)} />,
  ]
}

export const createRows = (awards: any) => {
  const rows = []
  if (!awards) {
    return rows
  }
  Object.keys(awards).map((devID: string) => {
    const currentData = awards?.[devID]?.[0]
    if (!currentData) {
      return false
    }
    rows.push(createData(currentData))
  })
  return rows
}

export const transformBtrAdditionalQuestionsChartData = (
  rawData,
  prevRawData,
  benchmarkRawData,
  LABELS,
  isBtr,
  period = 'Year',
) => {
  const preparedData = []
  if (!rawData) {
    return preparedData
  }

  let hasData = false

  Object.keys(rawData).map((id: string) => {
    try {
      if (parseInt(rawData[id])) {
        hasData = true
      }
    } catch {
      //
    }

    if (LABELS[id] && rawData[id] && rawData[id] !== '0.00') {
      preparedData.push({
        id: LABELS[id],
        'Current Rating': rawData[id],
        [`Previous ${period} Average`]: prevRawData?.[id] || 0,
        'UK Benchmark':
          benchmarkRawData?.[isBtr ? 'tenant' : 'owner']?.[id] || 0,
      })
    }
  })

  return hasData ? preparedData : []
}

export const transformAdditionalQuestionsChartData = (
  rawData,
  prevRawData,
  benchmarkRawData,
  LABELS,
  isBtr,
  period = 'Year',
) => {
  const preparedData = []
  if (!rawData) {
    return preparedData
  }

  let hasData = false

  Object.keys(rawData).map((id: string) => {
    try {
      if (parseInt(rawData[id])) {
        hasData = true
      }
    } catch {
      //
    }

    if (LABELS[id] && rawData[id] && rawData[id] !== '0.00') {
      preparedData.push({
        id: LABELS[id],
        'Current Rating': rawData?.[id] ? (rawData[id] * 100).toFixed(0) : 0,
        [`Previous ${period} Average`]: prevRawData?.[id]
          ? (prevRawData[id] * 100).toFixed(0)
          : 0,
        'UK Benchmark': (
          (benchmarkRawData?.[isBtr ? 'tenant' : 'owner']?.[id] || 0) * 100
        ).toFixed(0),
      })
    }
  })

  return hasData ? preparedData : []
}

export const transformReviewsCountHistoryData = (rawData) => {
  const preparedData = []
  let maxChartValue = 0
  if (!rawData) {
    return {
      data: null,
      maxChartValue,
    }
  }

  Object.keys(rawData).forEach((id: string) => {
    preparedData.push({
      x: id,
      y: rawData[id]?.count || 0,
    })
    if (rawData[id]?.count > maxChartValue) {
      maxChartValue = rawData[id].count
    }
  })

  if (preparedData.length) {
    return {
      data: [
        {
          id: 'Review count',
          color: CHART_BRAND_COLORS.green1,
          data: preparedData,
        },
      ],
      maxChartValue: maxChartValue * 1.1,
    }
  }

  return {
    data: preparedData,
    maxChartValue: maxChartValue * 1.1,
  }
}

export const RANKING_TYPE_COLORS_MAP = {
  rating: CHART_BRAND_COLORS.green1,
  rating_facilities: CHART_BRAND_COLORS.gold2,
  rating_location: CHART_BRAND_COLORS.darkBlue3,
  rating_value: CHART_BRAND_COLORS.blue4,
  rating_design: CHART_BRAND_COLORS.grey5,
  rating_management: CHART_BRAND_COLORS.green6,
}

export const transformReviewsRatingsHistoryData = (rawData: any) => {
  const preparedData = []
  if (!rawData || !Object.keys(rawData)?.length) {
    return preparedData
  }

  const partialData = {
    rating: [],
    rating_facilities: [],
    rating_location: [],
    rating_value: [],
    rating_design: [],
    rating_management: [],
  }

  Object.keys(rawData).map((id: string) => {
    const item = rawData[id]
    Object.keys(item).forEach((rating_key: string) => {
      if (partialData[rating_key]) {
        partialData[rating_key].push({
          x: id,
          y: item[rating_key],
          rating_count: item.rating_count,
        })
      }
    })
  })

  const finalData = []

  Object.keys(partialData).forEach((id: string) => {
    finalData.push({
      id: RATING_TYPES[id],
      color: RANKING_TYPE_COLORS_MAP[id],
      data: partialData[id],
    })
  })

  return finalData
}

export const transformOverallRatingsHistoryData = (
  rawData: any,
  accessor: string,
) => {
  const preparedData = []
  if (!rawData || !Object.keys(rawData)?.length) {
    return preparedData
  }

  Object.keys(rawData).map((id: string, devIndex: number) => {
    const partialData: any = {
      id: rawData[id].name,
      color: CHART_COLORS_NON_BENCHMARK_ARRAY[devIndex] || CHART_COLORS.black,
      data: [],
    }

    const item = rawData[id]?.data
    Object.keys(item).forEach((date: string) => {
      const value = item[date]?.[accessor]
      if (value && date) {
        partialData.data.push({
          x: date,
          y: value,
        })
      }
    })

    if (partialData.data.length) {
      preparedData.push(partialData)
    }
  })

  return preparedData
}

export const transformDevelopersOptions = (developers = []) => {
  if (!developers || !developers?.length) {
    return []
  }

  const options = []

  developers.forEach((developer: Company) => {
    options.push({
      value: developer.id,
      label: developer.name,
    })
  })

  return options
}

export const transformDevelopmentsOptions = (developments = []) => {
  if (!developments || !developments?.length) {
    return []
  }

  const options = []

  developments.forEach((developments: Development) => {
    options.push({
      value: developments.id,
      label: developments.name,
    })
  })

  return options
}

export const transformSingleDevelopmentAdditionalData = (data: any) => {
  if (!data) {
    return []
  }

  return [
    data.rating ? parseFloat(data.rating) : 0,
    data.rating_design ? parseFloat(data.rating_design) : 0,
    data.rating_facilities ? parseFloat(data.rating_facilities) : 0,
    data.rating_location ? parseFloat(data.rating_location) : 0,
    data.rating_value ? parseFloat(data.rating_value) : 0,
    data.rating_management ? parseFloat(data.rating_management) : 0,
  ].reverse()
}

export const getAwardsTableMetaData = (data: any) => {
  let filters = []
  if (!data) {
    return filters
  }

  if (!Object.keys(data)?.length) {
    return filters
  }

  Object.keys(data).map((id: string) => {
    const devAwards = data[id]
    if (devAwards?.length) {
      devAwards.map((award: any) => {
        if (award?.year) {
          filters.push(award.year)
        }
      })
    }
  })

  if (filters.length) {
    try {
      filters = [...new Set(filters)]
        .map((filter: any) => ({
          value: filter,
          label: filter,
        }))
        .sort((a: any, b: any) => {
          return b?.value - a?.value
        })
    } catch {
      return []
    }
  }

  return filters
}

export const filterAwardsData = (data: any, dateFilter: any) => {
  if (!data) {
    return []
  }

  if (!Object.keys(data)?.length) {
    return []
  }

  if (!dateFilter) {
    return data
  }

  Object.keys(data).forEach((id: string) => {
    const devAwards = data[id]
    if (devAwards?.length) {
      data[id] = devAwards.filter((award: any) => {
        if (award?.year !== dateFilter) {
          return false
        }
        return true
      })
    }
  })

  return data
}

export const transformReviewerAgesData = (rawData: any) => {
  const preparedData = []

  if (!rawData) {
    return preparedData
  }
  Object.keys(rawData).forEach((id: string) => {
    const item = rawData[id]
    if (REVIEWER_AGES?.[id]?.label) {
      preparedData.push({
        id: id,
        label: REVIEWER_AGES?.[id]?.label,
        value: item?.count || 0,
        color: REVIEWER_AGES?.[id]?.color,
      })
    }
  })

  return preparedData.sort((a, b) => {
    return REVIEWER_AGES[a.id].order - REVIEWER_AGES[b.id].order
  })
}
export const transformRatingsByReviewerAgeData = (rawData: any) => {
  const preparedData = []

  if (!rawData) {
    return preparedData
  }

  Object.keys(RATING_TYPES).map((ratingTypeId: string) => {
    const partialData = {
      id: ratingTypeId,
    }
    Object.keys(REVIEWER_AGES).map((reviewerAgeId: string) => {
      if (rawData?.[reviewerAgeId]?.[ratingTypeId]) {
        partialData[reviewerAgeId] = rawData[reviewerAgeId][ratingTypeId]
      }
    })
    if (Object.keys(partialData).length > 2) {
      preparedData.push(partialData)
    }
  })

  return preparedData
}

export const transformSustainabilityChartData = (rawData: any) => {
  const preparedData = []

  if (!rawData) {
    return preparedData
  }

  Object.keys(rawData).forEach((id: string) => {
    const value = rawData[id] ? Number(rawData[id]) : null

    if (SUSTAINABILITY?.[id]?.id) {
      preparedData.push({
        id: SUSTAINABILITY?.[id]?.id,
        label: SUSTAINABILITY?.[id]?.label,
        value: value * 100,
        color: SUSTAINABILITY?.[id]?.color,
      })
    }
  })

  return preparedData
}

export const transformOwnerReviewsData = (rawData: any) => {
  if (!rawData || !Object.keys(rawData).length) {
    return null
  }

  const preparedData = {
    ftb: rawData.first_time,
    htb: rawData.help_to_buy,
    nbb: rawData.new_build,
    all: rawData.total,
  }

  return preparedData
}

export const prepareDevelopmentScoresTableCsvData = (
  headCells: any,
  rows: any,
  benchmarkRow: any,
  appliedFiltersData: any[],
) => {
  const csvData = []

  if (!rows?.length) {
    return null
  }

  // first element is development name but it does not have head cell due to design
  let csvRow = ['"Development"']
  headCells.forEach((item: any) => {
    if (item.label) {
      csvRow.push(`"${item.label}"`)
    }
  })
  if (appliedFiltersData?.length) {
    csvRow.push('"Filters"')
  }

  csvData.push(csvRow.join(','))

  rows.forEach((row: any) => {
    csvRow = []

    if (row?.length) {
      row.forEach((item: any, innerIndex: number) => {
        // first element in row is id (hidden), it can be omitted from csv
        if (innerIndex > 0) {
          csvRow.push(`"${item}"`)
        }
      })
    }

    if (appliedFiltersData?.length) {
      let preparedFiltersCell = ''

      appliedFiltersData.forEach((item: any, index: number) => {
        if (index > 0) {
          preparedFiltersCell += ' | '
        }
        preparedFiltersCell += `${item.label}: ${item.value}`
      })
      csvRow.push('"' + preparedFiltersCell + '"')
    }

    csvData.push(csvRow.join(','))
  })

  const csvDataString = csvData.join('\n')

  return csvDataString
}
